import { FC, useMemo } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import Actions from "../Actions"
import { AddressInfo } from "../AddressInfo"
import EntraId from "../EntraId"
import FacilityId from "../FacilityId"
import useEntraActivation, { DialogMode } from "../useEntraActivation"
import { buildAddressPayload } from "../utils"
import { FacilityInfo } from "./FacilityInfo"
import Dialog from "@/components/shared/Dialog"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { fieldConfig } from "@/pages/ControlPanel/config"
import { AppDispatch, RootState } from "@/store"
import { fetchEntraConsentUrl, updateFacility } from "@/store/facilitiesSlice"
import { Facility, FacilityForm } from "@/types/facility"
import { ColorVariant } from "@/types/index"
import { convertCamelToKebab, getErrorMessage } from "@/utils/index"

interface FacilityListItemProps {
   facility: Facility
   editModeActiveRow: string
   deleteActiveRow: string
   isDisabledRow: boolean
   onFacilityClick: (id: string) => void
   onEditModeChange: (id: string) => void
   onAddUser: (id: string) => void
   onCreateFacility: () => void
   onDelete: () => void
}

const FacilityListItem: FC<FacilityListItemProps> = ({
   facility,
   editModeActiveRow,
   deleteActiveRow,
   isDisabledRow,
   onFacilityClick,
   onEditModeChange,
   onAddUser,
   onCreateFacility,
   onDelete,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const { loadingFacility, errorFacility } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )
   const { loadingCreateUser } = useSelector(
      ({ usersReducer }: RootState) => usersReducer
   )
   const { id, address, cells, wearables, users, name, alerts } = facility

   const isLoading = useMemo(() => {
      return loadingFacility || loadingCreateUser
   }, [loadingFacility, loadingCreateUser])

   const isEditMode = useMemo(() => {
      return editModeActiveRow === id
   }, [editModeActiveRow, id])

   const [facilityFormState, setState] = useFormStateHandler<Facility>(facility)

   const isActiveRow = useMemo(() => {
      return editModeActiveRow === id || deleteActiveRow === id
   }, [editModeActiveRow, deleteActiveRow, id])

   const isCountryValid = fieldConfig.country?.validate(
      facilityFormState.address.country || "USA"
   )
   const isEntraTidValid = fieldConfig.entraTid?.validate(
      facilityFormState.entraTid || ""
   )

   const facilityNameValue = facilityFormState.name
   const isFacilityNameEmpty =
      !facilityNameValue || facilityNameValue.trim().length === 0

   const isFormValid =
      !isFacilityNameEmpty &&
      isCountryValid &&
      isEntraTidValid &&
      deleteActiveRow !== id

   const handleSave = () => {
      const facilityId = facilityFormState.id || id
      const facilityDataToUpdate: FacilityForm = {
         name: facilityFormState.name,
         address: {
            ...buildAddressPayload(facilityFormState.address),
            country: facilityFormState.address.country || "USA",
         },
         ...(facilityFormState.entraTid && {
            entraTid: facilityFormState.entraTid,
         }),
      }

      if (isFormValid) {
         dispatch(
            updateFacility({ facilityId, facilityData: facilityDataToUpdate })
         ).then(() => {
            onEditModeChange("")
            if (facilityDataToUpdate.entraTid) {
               dispatch(fetchEntraConsentUrl(facilityId))
            }
         })
      }
   }

   const handleCancel = () => {
      setState(facility)
      onEditModeChange("")
   }

   const updateEntraFields = async (fields: Partial<FacilityForm>) => {
      await dispatch(
         updateFacility({
            facilityId: id,
            facilityData: { name: facilityFormState.name, ...fields },
         })
      ).unwrap()
   }

   const {
      entraDialogMode,
      entraActionLoading,
      openActivateDialog,
      openDeactivateDialog,
      closeEntraDialog,
      confirmEntraDialog,
   } = useEntraActivation({
      updateEntraFields,
      onDeactivated: () => onEditModeChange(""),
   })

   return (
      <div
         className={classNames("customers-facilities-table__group--facility", {
            "customers-facilities-table__group--loading": isLoading,
            "customers-facilities-table__group--error":
               errorFacility && deleteActiveRow === id,
         })}
      >
         {(isLoading && isActiveRow) || entraActionLoading ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : errorFacility && deleteActiveRow === id ? (
            getErrorMessage(errorFacility)
         ) : (
            <>
               <span className="customers-facilities-table__facility-info">
                  {Object.entries({ name, cells, wearables, users }).map(
                     ([key, value], i) => {
                        return (
                           <FacilityInfo
                              key={`${key}-x${i}`}
                              id={id}
                              alerts={alerts}
                              smallSize={key === "name"}
                              isEditMode={isEditMode}
                              className={`customers-facilities-table__facility-${convertCamelToKebab(key)}`}
                              infoKey={key}
                              info={value}
                              onChange={(e) => {
                                 setState({
                                    ...facilityFormState,
                                    [key]: e.target.value,
                                 })
                              }}
                              onClick={() => onFacilityClick(id)}
                              facilityFormState={facilityFormState}
                           />
                        )
                     }
                  )}
                  <AddressInfo
                     isEditMode={isEditMode}
                     id={id}
                     address={address}
                     onChange={(e) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              [e.target.name]: e.target.value,
                           },
                        })
                     }}
                     onStateChange={(selectedState) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              state: selectedState,
                           },
                        })
                     }}
                     onTimezoneChange={(timezone) => {
                        setState({
                           ...facilityFormState,
                           address: {
                              ...facilityFormState.address,
                              timezone,
                           },
                        })
                     }}
                     facilityFormState={facilityFormState}
                  />
                  <EntraId
                     entraTid={
                        facilityFormState.entraTid !== undefined
                           ? facilityFormState.entraTid
                           : facility.entraTid
                     }
                     consentUrl={facility.consentUrl}
                     entraSsoActive={facility.entraSsoActive}
                     errorFacility={errorFacility}
                     isEditMode={isEditMode}
                     onEntraTidChange={(e) => {
                        setState({
                           ...facilityFormState,
                           entraTid: e.target.value,
                        })
                     }}
                     onActivate={openActivateDialog}
                     onDeactivate={openDeactivateDialog}
                     onHover={() => dispatch(fetchEntraConsentUrl(id))}
                  />
                  <FacilityId facilityId={id} />
               </span>
               <Actions
                  isDisabled={isDisabledRow}
                  isEditMode={isEditMode}
                  isFormValid={isFormValid}
                  onDelete={() => onDelete()}
                  onSave={handleSave}
                  addFacility={onCreateFacility}
                  addUser={() => onAddUser(id)}
                  cancel={handleCancel}
                  onEditMode={() => onEditModeChange(id)}
                  showAddFacility={false}
               />
               {entraDialogMode && (
                  <Dialog
                     open
                     title="Are you sure you want to proceed?"
                     buttonTitle={
                        entraDialogMode === DialogMode.ACTIVATE
                           ? "Activate Entra ID login"
                           : "Deactivate Entra ID login"
                     }
                     buttonColor={
                        entraDialogMode === DialogMode.DEACTIVATE
                           ? ColorVariant.CERISE
                           : ColorVariant.HONOLULU_BLUE
                     }
                     handleClose={closeEntraDialog}
                     handleButtonClick={confirmEntraDialog}
                  />
               )}
            </>
         )}
      </div>
   )
}

export default FacilityListItem
